import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { IdleCheckSettings } from 'estensa-module';
import { seUserLogout } from '../../root-store/root.actions';
import { IdleCheckerDialogComponent, IdleDialogData, IdleDialogResult } from './idle-checker-dialog.component';
import { IdleLoginDialogComponent } from './idle-login-dialog.component';
import { ImagingService } from '../imaging/imaging.service';
import { EstensaBusWrapperService } from '../angularjs/estensa-bus-wrapper.service';
import { Subscription, filter } from 'rxjs';

export const HANDLED_EVENTS = ['click', 'keydown', 'mousemove', 'scroll'] as const;

export const CHECK_IDLE_INTERVAL = 1000;

@Injectable({
    providedIn: 'root',
})
export class IdleCheckerService {
    private lastActiveTime: number;
    private idleIntervalId: number;
    private recomedSubscription: Subscription;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private estensaBus: EstensaBusWrapperService,
        private dialog: MatDialog,
        private imaging: ImagingService,
        private store: Store,
    ) {}

    enable(settings: IdleCheckSettings) {
        for (const evt of HANDLED_EVENTS) {
            this.document.addEventListener(evt, this.resetIdleTimeout, {
                passive: true,
            });
        }
        this.recomedSubscription = this.estensaBus.busDataFired
            .pipe(filter((data) => data.messageType === 'ESTENSA_TEXT_TO_EDITOR'))
            .subscribe(() => this.resetIdleTimeout());

        this.resetIdleTimeout();
        this.idleIntervalId = window.setInterval(() => this.checkIdleTimeout(settings), CHECK_IDLE_INTERVAL);
    }

    disable() {
        for (const evt of HANDLED_EVENTS) {
            this.document.removeEventListener(evt, this.resetIdleTimeout);
        }
        this.recomedSubscription?.unsubscribe();

        window.clearInterval(this.idleIntervalId);
    }

    private resetIdleTimeout = () => {
        this.lastActiveTime = Date.now();
    };

    private checkIdleTimeout(settings: IdleCheckSettings) {
        const now = Date.now();
        const idleSeconds = (now - this.lastActiveTime) / 1000;
        if (idleSeconds > settings.timeoutSeconds) {
            this.disable();
            this.onIdleTimeout(settings);
        }
    }

    private onIdleTimeout(settings: IdleCheckSettings) {
        const dialogData: IdleDialogData = { gracePeriodSeconds: settings.gracePeriodSeconds };

        this.imaging.discardImages(false);

        this.dialog
            .open(IdleCheckerDialogComponent, {
                backdropClass: 'dialog-backdrop-opaque',
                closeOnNavigation: false,
                data: dialogData,
                disableClose: true,
                panelClass: 'dialog-panel-topmost',
            })
            .afterClosed()
            .subscribe((result: IdleDialogResult) => {
                if (result === 'login') {
                    this.login(settings);
                } else if (result === 'logout') {
                    this.store.dispatch(seUserLogout({}));
                } else {
                    this.enable(settings);
                }
            });
    }

    private login(settings: IdleCheckSettings) {
        this.dialog
            .open(IdleLoginDialogComponent, {
                backdropClass: 'dialog-backdrop-opaque',
                closeOnNavigation: false,
                disableClose: true,
                panelClass: 'dialog-panel-topmost',
            })
            .afterClosed()
            .subscribe(() => {
                this.enable(settings);
            });
    }
}
